import type { PreviewSessionSnapshot } from "@amoreg/contracts";

import type { RightPanelSurface } from "~/rightPanelStore";

/**
 * The panel's own tab bar, beside the cloud browser's tab strip.
 *
 * The cloud browser shows the browser's tabs in a strip of its own (the
 * page's title and icon on each), so the panel's tab for it is a plain
 * "Browser": one set of browser tabs on the screen, never two. The desktop
 * preview has no strip of its own; its panel tabs name their pages.
 */

/** A surface's name on its panel tab. */
export function surfaceTitle(
  surface: RightPanelSurface,
  sessions: Readonly<Record<string, PreviewSessionSnapshot>>,
  cloudBrowser: boolean,
): string {
  switch (surface.kind) {
    case "diff":
      return "Diff";
    case "files":
      return "Files";
    case "file":
      return surface.relativePath.slice(surface.relativePath.lastIndexOf("/") + 1);
    case "attachment":
      return surface.attachment.name;
    case "agents":
      return "Agents";
    case "preview": {
      if (cloudBrowser) return "Browser";
      const snapshot = surface.resourceId ? sessions[surface.resourceId] : null;
      if (!snapshot || snapshot.navStatus._tag === "Idle") return "Browser";
      if (snapshot.navStatus.title.trim().length > 0) return snapshot.navStatus.title;
      try {
        return new URL(snapshot.navStatus.url).host || "Browser";
      } catch {
        return "Browser";
      }
    }
  }
}

/** The page a panel tab takes its icon from; null for the plain globe. */
export function surfaceFaviconUrl(
  surface: Extract<RightPanelSurface, { kind: "preview" }>,
  sessions: Readonly<Record<string, PreviewSessionSnapshot>>,
  cloudBrowser: boolean,
): string | null {
  if (cloudBrowser) return null;
  const snapshot = surface.resourceId ? sessions[surface.resourceId] : null;
  return !snapshot || snapshot.navStatus._tag === "Idle" ? null : snapshot.navStatus.url;
}

/**
 * The surfaces the panel's tab bar lists. The cloud browser alone in the
 * panel has no panel tab: its own strip is the only set of tabs, and the bar
 * keeps its "+" menu and the panel's controls. With anything beside it, it
 * is listed (as "Browser") so the person can switch between them.
 */
export function tabBarSurfaces(
  surfaces: readonly RightPanelSurface[],
  cloudBrowser: boolean,
): readonly RightPanelSurface[] {
  if (cloudBrowser && surfaces.length === 1 && surfaces[0]?.kind === "preview") return [];
  return surfaces;
}
